import { Table } from '@getsynapse/design-system';
import classnames from 'classnames';
import useSnackbarNotification from 'Hooks/useSnackbarNotification';
import { get } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import { formatCurrency } from 'Pages/BudgetsPage/helpers/budgetHelper';
import useOptions from 'Pages/BudgetsPage/hooks/useOptions';
import useProjectLaborOfCost from 'Pages/BudgetsPage/hooks/useProjectLaborOfCost';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CSVLink } from 'react-csv';
import intl from 'react-intl-universal';
import { useDispatch, useSelector } from 'react-redux';
import { ParentOfLineItems } from 'utils/types/budget';
import { v4 as uuidv4 } from 'uuid';
import {
  addNewLineItem,
  deleteLineItem,
  fetchLineItemsAmountsByBudgetId,
  getBudgetViewType,
  getSelectedBudget,
  lineItemFilters,
  selectBudgetAmounts,
  selectCostOfLaborFilter,
  selectFilteredLineItems,
  setBudgetFilters,
  updateBudgetAmount,
  updateCostOfLabor,
  updateLineItem,
  updateLineItemNote,
} from '../../../../../state/BudgetList/budgetListSlice';
import {
  createFilterSetting,
  fetchFilterSettingByType,
  selectFiltersSettingsByType,
  updateFilterSetting,
} from '../../../../../state/Settings/Filters/FiltersSlice';
import {
  EDIT_BUDGET_TABS,
  MONTHS,
  SETTINGS_FILTERS,
} from '../../../../../utils/constants';
import {
  BUDGET_CSV_EXPORT_CONSTANTS,
  BUDGET_CSV_HEADER_SERIAL,
  BudgetFilters,
} from '../../../../../utils/constants/budget';
import {
  BudgetModel,
  CreateLineItemRequest,
  LineItemWithAmounts,
  objKeyAsString,
  TimeFrame,
  UpdateBudgetAmountRequest,
} from '../../../../../utils/customTypes';
import useBrokenBudgetOptions from '../../../hooks/useBrokenBudgetOptions';
import useBudgetLineItemCalculations from '../../../hooks/useBudgetLineItemCalculations';
import { DeleteLineItemModal } from '../components/modals/DeleteLineItemModal';
import AttachmentsSidePanel from '../components/sidepanels/AttachmentsSidePanel';
import NoteSidePanel from '../components/sidepanels/NoteSidePanel';
import BudgetPlanTableHeader from './BudgetPlanTableHeader';
import GeneratePlanTableHeaderData from './GeneratePlanTableHeaderData';
import { generateMultipleRows } from 'Pages/BudgetsPage/helpers/budgetRowHelper';
import { RenameBudgetModal } from '../../Manage/components/modals/RenameBudgetModal';
import { DuplicateBudgetModal } from '../../Manage/components/modals/DuplicateBudgetModal';

const BudgetPlanTable = forwardRef(() => {
  const dispatch = useDispatch();
  const selectedBudget = useSelector(getSelectedBudget);
  const { costOfLaborLineItems } = useProjectLaborOfCost();

  const budgetAmounts = useSelector(selectBudgetAmounts);
  const selectedBudgetView = useSelector(getBudgetViewType);
  const costOfLaborFilter = useSelector(selectCostOfLaborFilter);
  const budgetFilters = useSelector(lineItemFilters);
  const { parentsWithLineItems } = useBudgetLineItemCalculations();
  const lineItems = useSelector(selectFilteredLineItems);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [focusedRows, setFocusedRows] = useState<Record<string, boolean>>({});
  const [checkedRows, setCheckedRows] = useState<Record<string, boolean>>({});
  const [focusedCellId, setFocusedCellId] = useState<string>('');
  const [displayAttachmentModal, setDisplayAttachmentModal] = useState(false);
  const [displayDeleteModal, setDisplayDeleteModal] = useState(false);
  const [displayDuplicateBudgetModal, setDisplayDuplicateBudgetModal] =
    useState(false);
  const [displayRenameModal, setDisplayRenameModal] = useState(false);
  const [shouldDisplayNoteSidePanel, setShouldDisplayNoteSidePanel] =
    useState<boolean>(false);
  const [selectedBudgetInfo, setSelectedBudgetInfo] =
    useState<LineItemWithAmounts | null>(null);
  const [storedLineItem, setStoredLineItem] = useState<string>();
  const filtersSettings = useSelector(
    selectFiltersSettingsByType(SETTINGS_FILTERS.BUDGET_PLAN_TABLE)
  );
  const csvLinkRef = useRef<any>(null);
  const [exportFileName, setExportFileName] = useState('');
  const [csvHeaders, setCsvHeaders] = useState<
    { label: string; key: string }[]
  >([]);
  const [csvData, setCsvData] = useState<Record<string, any>[]>([]);

  const savedFilters = useMemo<BudgetFilters>(
    () => {
      if (
        filtersSettings &&
        filtersSettings.settings &&
        filtersSettings.settings.budgetId === selectedBudget?.id
      ) {
        return filtersSettings.settings;
      }
      return {};
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filtersSettings?.settings,
      filtersSettings?.settings?.budgetId,
      selectedBudget?.id,
    ] // Compare specific properties instead of the entire object
  );
  const { projectToProgramMap } = useBrokenBudgetOptions();

  useEffect(() => {
    dispatch(fetchFilterSettingByType(SETTINGS_FILTERS.BUDGET_PLAN_TABLE));
  }, [dispatch]);

  useEffect(() => {
    if (savedFilters) {
      dispatch(setBudgetFilters(savedFilters));
    }
  }, [savedFilters, dispatch]);

  const {
    vendorOptions,
    learningTeamOptions,
    programOptions,
    projectOptions,
    vendorLookup,
    learningTeamLookup,
    programLookup,
    projectLookup,
    filteredProjects,
    filteredPrograms,
  } = useOptions();

  // Tracks which program/project ids the Cost of Labor expansion below has
  // already auto-opened, so each id is expanded at most once. A set (rather than
  // a single boolean latch) lets a late-arriving programs/projects response —
  // the two fetches resolve in arbitrary order — still get expanded once it
  // lands, without re-expanding rows the user has since collapsed. Reset here
  // (this effect runs before the expansion effect in the same commit) so a
  // budget/view switch re-arms it.
  const costOfLaborExpandedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    setExpandedRows({});
    setFocusedRows({});
    setFocusedCellId('');
    costOfLaborExpandedIdsRef.current = new Set();
  }, [selectedBudgetView, selectedBudget]);

  // CGP-223: the Cost of Labor row renders as a child of an expanded project
  // row (and nested projects only render when their program parent is
  // expanded), so applying the filter with everything collapsed changed
  // nothing visibly and read as broken. Expand the program parents and their
  // projects whenever the filter is on for the current budget.
  useEffect(() => {
    // Which budget the *live* filter belongs to. `setBudgetFilters` carries the
    // budgetId synchronously — from `savedFilters` on mount and from the side
    // panel's Apply — so this updates in lockstep with `costOfLaborFilter`.
    // Fall back to the persisted setting for any legacy filter with no budgetId.
    const filterBudgetId =
      (budgetFilters as objKeyAsString | undefined)?.budgetId ??
      filtersSettings?.settings?.budgetId;
    const filterBelongsToSelectedBudget = filterBudgetId === selectedBudget?.id;

    // Only act while the filter for THIS budget is on. On a budget switch the
    // redux `costOfLaborFilter` still holds the previous budget's value for one
    // render (setBudgetFilters syncs after commit); keying the guard on the live
    // filter's budgetId keeps that stale value from expanding a freshly-selected
    // budget. Re-arm whenever the guard fails.
    if (!costOfLaborFilter || !filterBelongsToSelectedBudget) {
      costOfLaborExpandedIdsRef.current = new Set();
      return;
    }

    // Program parents must be open for their nested project rows — and each
    // project's Cost of Labor child — to render at all. Projects and programs
    // load asynchronously and in arbitrary order, so expand each id the first
    // time it appears: this catches the page-load race (filter on before the
    // lists populate) and a late-arriving programs response that re-nests a
    // project, while a later change won't re-expand rows the user has collapsed.
    if (!filteredProjects.length) {
      return;
    }

    const idsToExpand = [
      ...filteredPrograms
        .filter((program) => program.programProjects?.length)
        .map((program) => program.id),
      ...filteredProjects.map((project) => project.id),
    ];
    const newIds = idsToExpand.filter(
      (id) => !costOfLaborExpandedIdsRef.current.has(id)
    );
    if (!newIds.length) {
      return;
    }
    newIds.forEach((id) => costOfLaborExpandedIdsRef.current.add(id));

    setExpandedRows((prev) => ({
      ...prev,
      ...Object.fromEntries(newIds.map((id) => [id, true])),
    }));
  }, [
    costOfLaborFilter,
    selectedBudgetView,
    selectedBudget?.id,
    filteredProjects,
    filteredPrograms,
    budgetFilters,
    filtersSettings?.settings?.budgetId,
  ]);

  const {
    showNotification,
    isNotificationVisible,
    snackbarProps,
    SnackbarNotification,
  } = useSnackbarNotification();

  const setItemToDelete = async () => {
    if (selectedBudgetInfo == null) {
      return;
    }

    const removedLineItem = await dispatch(
      deleteLineItem(selectedBudgetInfo!.id)
    );
    if (get(removedLineItem, 'payload.data.id', null)) {
      showNotification({
        notificationVariant: 'success',
        notificationTitle: intl.get('SUCCESS_SNACKBAR_TITLE'),
        notificationMessage: intl.get(
          'BUDGET_PAGE.DELETE_BUDGET_MODAL.NOTIFICATIONS.BUDGET_DELETION_SUCCESS',
          { title: selectedBudgetInfo?.description || 'Budget Item' }
        ),
        hasActionButton: true,
      });
    } else if (get(removedLineItem, 'error', null)) {
      showNotification({
        notificationVariant: 'error',
        notificationTitle: intl.get(
          'BUDGET_PAGE.DELETE_BUDGET_MODAL.NOTIFICATIONS.ERROR_TITLE'
        ),
        notificationMessage: intl.get(
          'BUDGET_PAGE.DELETE_BUDGET_MODAL.NOTIFICATIONS.UNEXPECTED_ERROR',
          { title: selectedBudgetInfo?.description }
        ),
        hasActionButton: true,
      });
    }

    setDisplayDeleteModal(false);
  };

  function handleAddAttachment(itemId: string) {
    // to do
    setStoredLineItem(itemId);
    setDisplayAttachmentModal(true);
  }

  function onAddNewLineItem(
    parentType: string,
    parentId?: string,
    parentOfParentId?: string
  ) {
    let payload: {
      id?: string;
      learningTeamId?: string;
      programId?: string;
      vendorId?: string;
      projectId?: string;
    } = {};

    switch (selectedBudgetView?.value) {
      case EDIT_BUDGET_TABS.TEAMS:
        payload.learningTeamId = parentId;
        break;
      case EDIT_BUDGET_TABS.VENDORS:
        payload.vendorId = parentId;
        break;
      case EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT:
        if (parentOfParentId) {
          payload.programId = parentOfParentId;
        }
        if (parentType === 'projectId') {
          payload.projectId = parentId;
        } else if (parentType === 'programId') {
          payload.programId = parentId;
        }
        break;
      default:
        break;
    }
    const uuid = uuidv4();
    payload.id = uuid;
    setFocusedCellId(`${uuid}-description`);
    dispatch(addNewLineItem(payload));
  }

  const toggleFilterComponent = () =>
    setShouldDisplayNoteSidePanel((prevState) => !prevState);

  const onNoteSaved = async (note: string) => {
    const response = await dispatch(
      updateLineItemNote({
        id: storedNoteData!.id,
        note: note,
      })
    );
    if (get(response, 'payload.data')) {
      setShouldDisplayNoteSidePanel(false);
      showNotification({
        notificationVariant: 'success',
        notificationTitle: intl.get('SUCCESS_SNACKBAR_TITLE'),
        notificationMessage: intl.get(
          'BUDGET_PAGE.NOTIFICATIONS.NOTE_UPDATE_SUCCESS',
          { title: selectedBudgetInfo?.note }
        ),
        hasActionButton: true,
      });
    } else if (get(response, 'error', null)) {
      showNotification({
        notificationVariant: 'error',
        notificationTitle: intl.get(
          'BUDGET_PAGE.DELETE_BUDGET_MODAL.NOTIFICATIONS.ERROR_TITLE'
        ),
        notificationMessage: intl.get(
          'BUDGET_PAGE.DELETE_BUDGET_MODAL.NOTIFICATIONS.UNEXPECTED_ERROR',
          { title: selectedBudgetInfo?.note }
        ),
        hasActionButton: true,
      });
    }
  };

  async function onEditLineItem(
    lineItemReq: LineItemWithAmounts,
    field?: string,
    value?: string,
    amounts?: number[]
  ) {
    let lineItemRequest: any = {
      id: lineItemReq.id,
      budgetId: lineItemReq.budgetId,
    };
    if (amounts) {
      lineItemRequest.amounts = amounts;
    } else if (field) {
      lineItemRequest[field] = value;
      if (!value) {
        lineItemRequest[field] = null;
      }
    }

    if (field === 'description') {
      lineItemRequest.learningTeamId = lineItemReq.learningTeamId;
      lineItemRequest.vendorId = lineItemReq.vendorId;
      lineItemRequest.programId = lineItemReq.programId;
      lineItemRequest.projectId = lineItemReq.projectId;
    }

    await dispatch(updateLineItem(lineItemRequest));
  }

  async function onEditCostOfLabor(
    projectId: string,
    lineItemId?: string,
    amounts?: number[]
  ) {
    let lineItemRequest: any = {
      projectId: projectId,
    };
    if (amounts) {
      lineItemRequest.amounts = amounts;
    }
    lineItemRequest.id = lineItemId || '';
    lineItemRequest.budgetId = selectedBudget!.id;
    await dispatch(updateCostOfLabor(lineItemRequest));
  }

  async function onEditBudgetAmount(
    budgetAmountReq: UpdateBudgetAmountRequest
  ) {
    await dispatch(updateBudgetAmount(budgetAmountReq));
  }

  const onLineItemSaved = async (lineItemRequest: CreateLineItemRequest) => {
    lineItemRequest.id = await uuidv4();
    lineItemRequest.budgetId = selectedBudget!.id;
    const response = await dispatch(updateLineItem(lineItemRequest));
    if (get(response, 'payload.data')) {
      await dispatch(fetchLineItemsAmountsByBudgetId(selectedBudget!.id));
      showNotification({
        notificationTitle: intl.get('SUCCESS_SNACKBAR_TITLE'),
        notificationMessage: intl.get(
          'BUDGET_PAGE.NOTIFICATIONS.NOTE_UPDATE_SUCCESS',
          { title: selectedBudgetInfo?.description }
        ),
        hasActionButton: true,
      });
    }
  };

  const [storedNoteData, setStoredNoteData] =
    useState<LineItemWithAmounts | null>(null);

  function handleAddNote(item: LineItemWithAmounts) {
    setStoredNoteData(item);
    setShouldDisplayNoteSidePanel(true);
  }

  function handleDeleteLineItem(item: LineItemWithAmounts) {
    setSelectedBudgetInfo(item);
    setDisplayDeleteModal(true);
  }

  const toggleExpandedRow = (rowId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const toggleFocusedRow = ({
    rowId,
    isFocused,
    isOnlyChecked = false,
  }: {
    rowId: string;
    isFocused?: boolean;
    isOnlyChecked?: boolean;
  }) => {
    if (!isOnlyChecked) {
      setFocusedRows((prev) => ({
        ...prev,
        [rowId]: isFocused == null ? !prev[rowId] : isFocused,
      }));
    }

    setCheckedRows((prev) => ({
      ...prev,
      [rowId]: isFocused == null ? !prev[rowId] : isFocused,
    }));
  };

  const handleSaveFiltersSettings = (updatedFilters: BudgetFilters) => {
    const filterSettingsBudgetId = filtersSettings?.settings?.budgetId;
    if (
      filtersSettings &&
      filtersSettings.id &&
      filterSettingsBudgetId === selectedBudget?.id
    ) {
      const recalculatedUpdatedFilters =
        Object.keys(updatedFilters).length > 0
          ? {
              budgetId: selectedBudget?.id,
              ...updatedFilters,
            }
          : { ...updatedFilters };
      dispatch(
        updateFilterSetting({
          id: filtersSettings.id,
          updateFields: {
            filter_settings: recalculatedUpdatedFilters,
          },
        })
      );
    } else {
      dispatch(
        createFilterSetting({
          filter_type: SETTINGS_FILTERS.BUDGET_PLAN_TABLE,
          filter_settings: { ...updatedFilters, budgetId: selectedBudget?.id },
        })
      );
    }
  };

  const exportCsvData = () => {
    const year = selectedBudget
      ? new Date(selectedBudget.fiscalPeriodStart).getUTCFullYear()
      : new Date().getFullYear();

    const yearStart = selectedBudget
      ? new Date(selectedBudget.fiscalPeriodStart).getUTCMonth()
      : 0;

    const timeFrame = selectedBudget?.timeFrame ?? TimeFrame.QUARTERLY;

    let data: Record<string, any>[] = [];

    const headersKeys =
      BUDGET_CSV_HEADER_SERIAL[
        selectedBudgetView?.value as keyof typeof BUDGET_CSV_HEADER_SERIAL
      ] || [];

    let newHeaders = headersKeys.map((key) => ({
      label: intl.get(`BUDGET_PAGE.EDIT.TABLE.HEAD.${key}`),
      key: BUDGET_CSV_EXPORT_CONSTANTS[key].KEY,
    }));

    newHeaders.push({ label: `Fiscal Year ${year}`, key: 'fiscalYear' });

    if (timeFrame === TimeFrame.QUARTERLY) {
      newHeaders.push(
        { label: `Q1 ${year}`, key: '0' },
        { label: `Q2 ${year}`, key: '1' },
        { label: `Q3 ${year}`, key: '2' },
        { label: `Q4 ${year}`, key: '3' }
      );
    } else if (timeFrame === TimeFrame.MONTHLY) {
      for (let i = 0; i < 12; i++) {
        const calendarYear = yearStart + i < 12 ? year : year + 1;
        newHeaders.push({
          label: `${MONTHS[(yearStart + i) % 12]} ${calendarYear}`,
          key: `${(yearStart + i) % 12}`,
        });
      }
    }
    newHeaders.push({
      label: intl.get('BUDGET_PAGE.EDIT.TABLE.HEAD.NOTES'),
      key: BUDGET_CSV_EXPORT_CONSTANTS.NOTES.KEY,
    });

    data = generateCsvDataFromParent(
      parentsWithLineItems,
      yearStart,
      timeFrame
    );

    const formattedDate = new Date().toDateString();
    setExportFileName(
      `budget_plan_exports_${
        selectedBudget?.title
      }_${uuidv4()}_${formattedDate}.csv`
    );

    setCsvHeaders(newHeaders);
    setCsvData(data);

    setTimeout(() => {
      if (csvLinkRef.current) {
        csvLinkRef.current.link.click();
      }
    }, 1000);
  };

  function generateCsvDataFromParent(
    parents: ParentOfLineItems[],
    fiscalYearStart: number,
    timeFrame: TimeFrame
  ) {
    let data: Record<string, any>[] = [];

    parents.forEach((parent) => {
      parent.lineItems?.forEach((lineItem) => {
        let row: Record<string, any> = {};

        row[BUDGET_CSV_EXPORT_CONSTANTS.DESCRIPTION.KEY] = lineItem.description;
        row[BUDGET_CSV_EXPORT_CONSTANTS.TEAM.KEY] = lineItem.learningTeamId
          ? learningTeamLookup[lineItem.learningTeamId]
          : '';
        row[BUDGET_CSV_EXPORT_CONSTANTS.PROGRAM.KEY] = lineItem.programId
          ? programLookup[lineItem.programId]
          : '';
        row[BUDGET_CSV_EXPORT_CONSTANTS.PROJECT.KEY] = lineItem.projectId
          ? projectLookup[lineItem.projectId]
          : '';
        row[BUDGET_CSV_EXPORT_CONSTANTS.BUDGET_SOURCE.KEY] =
          lineItem.budgetSource;
        row[BUDGET_CSV_EXPORT_CONSTANTS.VENDOR.KEY] = lineItem.vendorId
          ? vendorLookup[lineItem.vendorId]
          : '';

        // Cost of Labor rows are derived from projectsActualLaborCost and
        // only carry values in rawActuals -- rawAmounts is 0 by construction.
        // Reading rawAmounts here would export $0 for every CoL row and, via
        // the drop condition below, omit them from the CSV entirely even when
        // the row is showing real values on screen. Match the row template's
        // read side (generateCostOfLaborRow).
        const isCostOfLaborRow = lineItem.description === 'Cost of Labor';
        const rowValues = isCostOfLaborRow
          ? lineItem.rawActuals
          : lineItem.rawAmounts;

        const fiscalYearTotal = rowValues.reduce(
          (total: number, item: number) => total + (item ?? 0),
          0
        );

        row[BUDGET_CSV_EXPORT_CONSTANTS.FISCAL_YEAR.KEY] =
          formatCurrency(fiscalYearTotal);

        if (timeFrame === TimeFrame.QUARTERLY) {
          Array.from({ length: 4 }).forEach((_, index) => {
            row[`${index}`] = formatCurrency(rowValues[index] ?? 0);
          });
        } else if (timeFrame === TimeFrame.MONTHLY) {
          Array.from({ length: 12 }).forEach((_, index) => {
            row[`${index}`] = formatCurrency(rowValues[index] ?? 0);
          });
        }

        row[BUDGET_CSV_EXPORT_CONSTANTS.NOTES.KEY] = lineItem.note;

        if (!(isCostOfLaborRow && fiscalYearTotal === 0)) {
          data.push(row);
        }
      });

      const childData = generateCsvDataFromParent(
        parent.children ?? [],
        fiscalYearStart,
        timeFrame
      );
      data.push(...childData);
    });

    return data;
  }

  const onCreateBudget = useCallback(
    (data: BudgetModel) => {
      try {
        setDisplayDuplicateBudgetModal(false);
        showNotification({
          notificationTitle: intl.get('SUCCESS_SNACKBAR_TITLE'),
          notificationMessage: intl.get(
            'BUDGET_PAGE.CREATE_BUDGET_MODAL.NOTIFICATIONS.BUDGET_CREATION_SUCCESS',
            { title: data.title }
          ),
          hasActionButton: true,
        });
      } catch (error) {
        showNotification({
          notificationTitle: intl.get(
            'BUDGET_PAGE.DELETE_BUDGET_MODAL.NOTIFICATIONS.ERROR_TITLE'
          ),
          notificationMessage: intl.get(
            'BUDGET_PAGE.CREATE_BUDGET_MODAL.NOTIFICATIONS.BUDGET_CREATION_ERROR',
            { title: data.title }
          ),
          hasActionButton: false,
        });
      }
    },
    [showNotification]
  );

  const onRenameBudget = useCallback(
    (success: boolean) => {
      if (!success) {
        showNotification({
          notificationVariant: 'error',
          notificationTitle: intl.get(
            'BUDGET_PAGE.RENAME_BUDGET_MODAL.NOTIFICATIONS.ERROR_TITLE'
          ),
          notificationMessage: intl.get(
            'BUDGET_PAGE.RENAME_BUDGET_MODAL.NOTIFICATIONS.ERROR_MSG'
          ),
          hasActionButton: false,
        });
      } else {
        setDisplayRenameModal(false);
      }
    },
    [showNotification]
  );

  return (
    <div className='h-full flex flex-col'>
      <BudgetPlanTableHeader
        selectedBudgetId={selectedBudget?.id}
        appliedFilters={savedFilters}
        saveFilters={handleSaveFiltersSettings}
        filterButtonEnabled={!isEmpty(parentsWithLineItems)}
        exportCsvData={exportCsvData}
        onDuplicateBudget={() => setDisplayDuplicateBudgetModal(true)}
        onRenameBudget={() => setDisplayRenameModal(true)}
      />
      <div
        className={classnames('w-full rounded-b mb-8', {
          'overflow-auto border border-neutral-lighter-two ': true,
          'max-h-no-filtered-budget-table-body': isEmpty(savedFilters),
          'max-h-filtered-budget-table-body': !isEmpty(savedFilters),
        })}
      >
        <Table
          canSelectRows={false}
          data-cy='budget-plan-table'
          className='relative w-full border-0 custom-table-spacing'
          data={{
            total: parentsWithLineItems.length,
            headData: GeneratePlanTableHeaderData({
              selectedBudget,
              selectedBudgetView,
            }),
            rows: generateMultipleRows(
              parentsWithLineItems,
              0,
              expandedRows,
              toggleExpandedRow,
              focusedRows,
              toggleFocusedRow,
              checkedRows,
              handleAddNote,
              handleAddAttachment,
              handleDeleteLineItem,
              onLineItemSaved,
              onAddNewLineItem,
              onEditLineItem,
              onEditCostOfLabor,
              programOptions,
              projectToProgramMap,
              vendorOptions,
              learningTeamOptions,
              projectOptions,
              selectedBudgetView?.value,
              selectedBudget
                ? new Date(selectedBudget.fiscalPeriodStart).getUTCMonth()
                : 0,
              selectedBudget?.timeFrame ?? TimeFrame.QUARTERLY,
              lineItems,
              budgetAmounts,
              onEditBudgetAmount,
              costOfLaborFilter,
              costOfLaborLineItems,
              focusedCellId,
              setFocusedCellId,
              selectedBudget
            ).flat(),
          }}
        />
      </div>

      {isNotificationVisible && snackbarProps && (
        <SnackbarNotification {...snackbarProps} />
      )}

      <DeleteLineItemModal
        isOpen={displayDeleteModal}
        closeModal={() => setDisplayDeleteModal(false)}
        onCancel={() => setDisplayDeleteModal(false)}
        title={selectedBudgetInfo?.description ?? ''}
        onDeleteProgram={() => setItemToDelete()}
      />

      <AttachmentsSidePanel
        open={displayAttachmentModal}
        lineItemId={storedLineItem}
        onClose={() => {
          setDisplayAttachmentModal(false);
        }}
      />

      <NoteSidePanel
        open={shouldDisplayNoteSidePanel}
        onClose={toggleFilterComponent}
        onNoteSaved={onNoteSaved}
        title={storedNoteData?.description ?? ''}
        note={storedNoteData?.note ?? ''}
      />

      <CSVLink
        data={csvData}
        headers={csvHeaders}
        filename={exportFileName}
        ref={csvLinkRef}
        hidden={true}
      >
        Export
      </CSVLink>

      {selectedBudget && (
        <DuplicateBudgetModal
          fromBudget={{
            id: selectedBudget.id,
            name: selectedBudget.title,
            year_start: new Date(selectedBudget.fiscalPeriodStart),
          }}
          isOpen={displayDuplicateBudgetModal}
          closeModal={() => setDisplayDuplicateBudgetModal(false)}
          onCancel={() => setDisplayDuplicateBudgetModal(false)}
          onDuplicateBudget={onCreateBudget}
        />
      )}

      {selectedBudget && (
        <RenameBudgetModal
          id={selectedBudget.id}
          currentName={selectedBudget.title}
          isOpen={displayRenameModal}
          closeModal={() => setDisplayRenameModal(false)}
          onCancel={() => setDisplayRenameModal(false)}
          onRenameBudget={onRenameBudget}
        />
      )}
    </div>
  );
});

export default BudgetPlanTable;
